import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { X, ArrowRight, ArrowLeft, Sparkle } from '@phosphor-icons/react';

/**
 * ProductTour — a 6-step first-login walkthrough.
 *
 * Trigger: shown automatically once when `user.tour_completed_at` is unset.
 * Persistence: a single POST to /api/auth/tour-completed marks it done forever.
 * Fallback: localStorage flag so a stale /me doesn't re-trigger on next refresh.
 *
 * Each step targets a `data-testid` already present in Layout/AIAssistant.
 * The spotlight is a CSS `box-shadow` cutout — no extra dependency.
 */

const STEPS = [
  // 0: welcome — centered modal, no spotlight
  {
    welcome: true,
    title: "Welcome to SalesCrunchCRM",
    body: "Quick 30-second tour so you know where everything lives. You can skip anytime.",
  },
  {
    target: '[data-testid="nav-contacts"]',
    title: "Your contacts",
    body: "The ACT! 4.0 contact card — name, phone, address, notes, activity history, all on one screen.",
    placement: "right",
    route: "/contact",
  },
  {
    target: '[data-testid="nav-search"]',
    title: "Boolean Lookup",
    body: "Filter by any field — \"Company starts with A AND state = TX\". Save the result as a Smart Group.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-groups"]',
    title: "Smart Groups (pre-loaded)",
    body: "We seeded 4 smart groups for you: Customers, Hot Leads, Needs Follow-Up, Recently Added. Open any to see live members.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-templates"]',
    title: "8 starter email templates",
    body: "Quick Follow-Up, Meeting Confirmation, Birthday Greeting, and 5 more — all ready with merge fields like {first_name}.",
    placement: "right",
  },
  {
    target: '[data-testid="sidebar-quick-add"]',
    title: "Quick Add (Cmd / Ctrl + K)",
    body: "Spawn a contact, activity, or note from anywhere in the app — no clicking required.",
    placement: "right",
  },
  {
    target: '[data-testid="ai-assistant-open"]',
    title: "Ask Crunchy",
    body: "Your AI sidekick. Ask \"how do I print labels?\" or \"show me leads I haven't called in 60 days\" — Crunchy knows the product cold.",
    placement: "left",
  },
  // last: final modal — no spotlight; primary CTA deep-links to /contact so
  // tour-watchers actually start their first action (measurable activation).
  {
    finale: true,
    title: "You're set",
    body: "Jump into your contacts and add your first one — or click the magnifying-glass to import a CSV from your old CRM. Crunchy's always one click away if you get stuck.",
    primaryCta: { label: "Take me to my contacts", route: "/contact" },
    secondaryCta: { label: "I'll explore on my own", route: null },
  },
];

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 14;
const TOOLTIP_W = 320;

export default function ProductTour() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [active, setActive] = useState(false);
  const [rect, setRect] = useState(null);

  // Decide whether to auto-start on mount
  useEffect(() => {
    if (!user || user === false) return;
    if (user.tour_completed_at) return;
    if (localStorage.getItem('tour_completed_v1') === '1') return;
    // Slight delay so the sidebar finishes mounting & data-testids exist
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, [user]);

  const current = STEPS[step];

  // Re-measure the target element each step / on resize / scroll
  const measure = useCallback(() => {
    if (!active || !current?.target) { setRect(null); return; }
    const el = document.querySelector(current.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [active, current]);

  useEffect(() => {
    if (!active) return;
    // If step has a route, push it first (waits for next tick to measure)
    if (current?.route) navigate(current.route);
    const id = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, step, measure, current, navigate]);

  const finish = async () => {
    setActive(false);
    localStorage.setItem('tour_completed_v1', '1');
    try {
      await api.post('/auth/tour-completed');
      if (refreshUser) await refreshUser();
    } catch (e) {
      // Non-fatal — localStorage flag will still prevent re-trigger
    }
  };

  if (!active || !current) return null;

  // Centered modal (welcome + finale)
  if (current.welcome || current.finale) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="product-tour">
        <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
        <div
          className="relative bg-white border border-[#999] shadow-2xl w-[420px] max-w-[90vw] overflow-hidden"
          data-testid="tour-welcome"
        >
          <div className="px-5 py-3 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-[#F39C12]" />
            <span className="text-[13px] font-bold tracking-wide uppercase">
              {current.welcome ? 'Quick Tour' : 'All Set'}
            </span>
            <div className="flex-1" />
            <button onClick={finish} className="text-white/70 hover:text-white" aria-label="Close" data-testid="tour-close">
              <X size={16} weight="bold" />
            </button>
          </div>
          <div className="px-5 py-5">
            <h2 className="text-[18px] font-bold text-[#333] mb-2">{current.title}</h2>
            <p className="text-[13px] text-[#555] leading-relaxed">{current.body}</p>
          </div>
          <div className="px-5 py-3 bg-[#F5F5F0] border-t border-[#DDD] flex items-center justify-end gap-2">
            {current.welcome ? (
              <>
                <button
                  onClick={finish}
                  className="px-3 py-1.5 text-[12px] text-[#666] hover:text-[#333]"
                  data-testid="tour-skip-welcome"
                >
                  Skip tour
                </button>
                <button
                  onClick={() => {
                    setStep(1);
                    api.post('/funnel/event', { event: 'tour_started' }).catch(() => {});
                  }}
                  className="px-4 py-1.5 text-[12px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] flex items-center gap-1.5"
                  data-testid="tour-start"
                >
                  Start tour <ArrowRight size={12} weight="bold" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={finish}
                  className="px-3 py-1.5 text-[12px] text-[#666] hover:text-[#333]"
                  data-testid="tour-finale-skip"
                >
                  {current.secondaryCta?.label || "I'll explore on my own"}
                </button>
                <button
                  onClick={async () => {
                    // Fire-and-forget activation event so we can measure how
                    // many tour-watchers actually click into the CRM.
                    api.post('/funnel/event', { event: 'tour_finale_cta_clicked' }).catch(() => {});
                    await finish();
                    if (current.primaryCta?.route) navigate(current.primaryCta.route);
                  }}
                  className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] flex items-center gap-1.5"
                  data-testid="tour-done"
                >
                  {current.primaryCta?.label || 'Got it'} <ArrowRight size={12} weight="bold" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Spotlight step
  const padded = rect
    ? {
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      }
    : null;

  // Tooltip position with viewport-aware clamping
  let tooltipStyle = { top: 80, left: 80 };
  if (padded) {
    const TOOLTIP_H_EST = 170; // approximate
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = padded.top;
    let left;
    if (current.placement === 'left') {
      left = padded.left - TOOLTIP_W - TOOLTIP_GAP;
      if (left < 20) left = padded.left + padded.width + TOOLTIP_GAP; // flip right
    } else {
      left = padded.left + padded.width + TOOLTIP_GAP;
      if (left + TOOLTIP_W > vw - 20) left = Math.max(20, padded.left - TOOLTIP_W - TOOLTIP_GAP);
    }
    // Clamp vertically so the tooltip always fits in viewport
    top = Math.max(20, Math.min(top, vh - TOOLTIP_H_EST - 20));
    tooltipStyle = { top, left: Math.max(20, left) };
  }

  const isLast = step === STEPS.length - 2; // last spotlight before finale
  const totalSpotlights = STEPS.filter(s => !s.welcome && !s.finale).length;
  const currentSpotlightIdx = step; // since step 0 = welcome, spotlight 1..N

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="product-tour">
      {/* Dim overlay with cutout via box-shadow */}
      {padded ? (
        <div
          className="absolute pointer-events-auto"
          style={{
            top: padded.top,
            left: padded.left,
            width: padded.width,
            height: padded.height,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
            borderRadius: 6,
            transition: 'all 200ms ease',
          }}
          onClick={() => { /* swallow clicks on spotlight */ }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/55 pointer-events-auto" />
      )}

      {/* Tooltip */}
      <div
        className="absolute bg-white border border-[#999] shadow-2xl pointer-events-auto"
        style={{ ...tooltipStyle, width: TOOLTIP_W }}
        data-testid={`tour-step-${currentSpotlightIdx}`}
      >
        <div className="px-4 py-2 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#F39C12]" />
          <span className="text-[11px] font-bold tracking-wide uppercase">
            Step {currentSpotlightIdx} of {totalSpotlights}
          </span>
          <div className="flex-1" />
          <button onClick={finish} className="text-white/70 hover:text-white" aria-label="Close tour" data-testid="tour-close">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="px-4 py-3">
          <h3 className="text-[14px] font-bold text-[#333] mb-1">{current.title}</h3>
          <p className="text-[12px] text-[#555] leading-relaxed">{current.body}</p>
        </div>
        <div className="px-4 py-2 bg-[#F5F5F0] border-t border-[#DDD] flex items-center gap-2">
          <button
            onClick={finish}
            className="text-[11px] text-[#666] hover:text-[#333]"
            data-testid="tour-skip"
          >
            Skip
          </button>
          <div className="flex-1" />
          {step > 1 && (
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              className="px-2.5 py-1 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F0F0F0] flex items-center gap-1"
              data-testid="tour-prev"
            >
              <ArrowLeft size={11} weight="bold" /> Back
            </button>
          )}
          <button
            onClick={() => setStep(s => s + 1)}
            className="px-3 py-1 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] flex items-center gap-1"
            data-testid="tour-next"
          >
            {isLast ? 'Finish' : 'Next'} <ArrowRight size={11} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
